/**
 * Admin → Users (T1.1, 2026-05-28)
 *
 * Discovery-native user management surface. Closes the Discovery
 * Independence Principle gap (CLAUDE.md §14) that left standalone
 * tenants without an in-Discovery way to LIST who exists or INVITE
 * a new admin without going through Core / their IdP.
 *
 * Backend shipped earlier this session in commit c409666:
 *   GET    /v1/admin/users
 *   GET    /v1/admin/users/:id
 *   POST   /v1/admin/users          (invite - creates row status='invited')
 *   PATCH  /v1/admin/users/:id      (role / status)
 *   DELETE /v1/admin/users/:id      (soft-delete → status='suspended')
 *
 * Layout: 4 KPI tiles + filter bar + users table. Per-row actions:
 * Edit Role · Suspend / Activate · Delete. Invite-User dialog
 * collects email + full name + role.
 *
 * The 7 selectable roles are the tenant-assignable roles. platform_* and
 * edge_pod are NOT listed - those are platform-internal accounts, not
 * tenant grants.
 *
 * Leaving them out of this <select> is presentation, NOT enforcement. Until
 * 2026-09-14 it was the only thing stopping a DPO or IT Admin from sending
 * `role: 'platform_super_admin'` straight to the API. The server now refuses
 * any role outside apps/api-controller/src/shared/tenant-assignable-roles.ts
 * with a 400, and apiFetch shows that problem's `detail` in the error banner.
 *
 * CONSENT-MANAGED PEOPLE (30 Oct 2026). For a tenant linked to FOCTTA Consent,
 * Consent owns people and roles. The people who arrive from Consent are listed
 * (the hand-off keeps their row), shown read-only with "Managed in FOCTTA
 * Consent" and a link; the API refuses role change, suspend and delete for them
 * (409), so hiding the controls is presentation only. "Invite user" sends the
 * administrator to invite in Consent. An invited row from before that is not
 * Consent-managed is shown as never activated: no invitation was ever sent
 * from here, and the person's first hand-off from Consent takes the row over.
 * On a standalone tenant the invitation is really emailed, and the page says
 * so, or says that mail is not configured.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  Lock,
  Mail,
  Plus,
  RefreshCw,
  RotateCcw,
  Shield,
  Trash2,
  UserCheck,
  Users as UsersIcon,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listAdminUsers,
  inviteAdminUser,
  patchAdminUser,
  deleteAdminUser,
  resetAdminUserMfa,
  type AdminInvitationResult,
  type AdminMfaResetResult,
  type AdminUsersConsentLink,
  type DiscoveryUser,
  type DiscoveryUserStatus,
} from '@/lib/api';

// ── Role catalog ────────────────────────────────────────────────
//
// The 6 standard tenant roles per CLAUDE.md "Tenant Roles" + custom_role.
// A subset of the server's TENANT_ASSIGNABLE_ROLES (not of the 0075 CHECK,
// which also admits platform and service roles). The deprecated
// alias `read_only_security_ops` is accepted by backend for back-compat
// but NOT offered here (canonical name only).

const SELECTABLE_ROLES = [
  { key: 'dpo', label: 'DPO' },
  { key: 'compliance_analyst', label: 'Compliance Analyst' },
  { key: 'legal_reviewer', label: 'Legal Reviewer' },
  { key: 'it_admin', label: 'IT Admin' },
  { key: 'security_operator', label: 'Security Operator' },
  { key: 'read_only_security_operations', label: 'Read-Only Security Operations' },
  { key: 'custom_role', label: 'Custom Role' },
] as const;

const ROLE_LABEL: Record<string, string> = Object.fromEntries(
  SELECTABLE_ROLES.map((r) => [r.key, r.label]),
);

const STATUS_VARIANT: Record<DiscoveryUserStatus, 'success' | 'warning' | 'critical'> = {
  active: 'success',
  invited: 'warning',
  suspended: 'critical',
};
const STATUS_ICON: Record<DiscoveryUserStatus, typeof CheckCircle2> = {
  active: UserCheck,
  invited: Clock,
  suspended: Ban,
};

/**
 * What the MFA column offers for a person (final gate of PR #84, F7).
 *
 * The reset used to be shown only to people who had FINISHED enrolling. A
 * person locked out of the MFA step (five wrong codes) or stuck with a staged,
 * never-confirmed secret was not enrolled, so there was no button, and an
 * administrator could only unlock them in SQL. The server's reset clears all
 * three (factor, staged secret, lock), so the page offers it for all three.
 */
export type MfaAdminAction = 'reset' | 'clear' | null;

export function mfaAdminAction(u: DiscoveryUser, now: number = Date.now()): MfaAdminAction {
  if (u.mfaEnrolledAt) return 'reset';
  const locked = !!u.mfaLockedUntil && new Date(u.mfaLockedUntil).getTime() > now;
  if (locked || u.mfaSecretStaged) return 'clear';
  return null;
}

/** One line for the administrator after a reset: what it did, and what did not happen. */
export function describeMfaReset(email: string, r: AdminMfaResetResult): string {
  const parts = [`MFA reset for ${email}.`];
  const n = r.linksRevoked ?? 0;
  if (n > 0) parts.push(`${n} outstanding sign-in link${n === 1 ? '' : 's'} revoked.`);
  if (r.passwordCleared) {
    parts.push('Password cleared.');
    if (r.resetLink?.sent && r.resetLink.provider === 'mock') {
      parts.push('Mail is not configured here: the reset link was written to the controller log, not emailed.');
    } else if (r.resetLink?.sent) {
      parts.push('A reset link was emailed to them.');
    } else if (r.resetLink?.reason === 'account_not_active') {
      parts.push('No link was sent because the account is not active: reinstate it, then reset again with a new password.');
    } else if (r.resetLink?.reason === 'link_failed' || r.resetLink?.reason === 'tenant_not_found') {
      parts.push('The reset link could not be created: have them use Forgot password on the sign-in page, or reset again.');
    } else {
      parts.push('The reset link could not be emailed: reset again, or have them use Forgot password.');
    }
  }
  return parts.join(' ');
}

/** FOCTTA Consent keeps this person: read-only here. */
export function isManagedInConsent(u: Pick<DiscoveryUser, 'managedBy'>): boolean {
  return u.managedBy === 'foctta_core';
}

/**
 * An invited row on a Consent-linked tenant that Consent does not keep: made by
 * this page before 30 Oct 2026, when the invite sent nothing. It was never
 * activated and cannot be from here; the person's first hand-off from Consent
 * takes it over.
 */
export function isUnsentInvitation(
  u: Pick<DiscoveryUser, 'managedBy' | 'status'>,
  consent: Pick<AdminUsersConsentLink, 'linked'> | null,
): boolean {
  return !!consent?.linked && u.status === 'invited' && !isManagedInConsent(u);
}

/**
 * A Consent-managed person whose suspension an administrator may lift here: a
 * Consent leaver signal or their own. Not 'core_role': Consent sends a role
 * Discovery cannot bridge, and only Consent's next hand-off lifts that.
 */
export function mayReinstateManaged(u: Pick<DiscoveryUser, 'managedBy' | 'status' | 'suspendedBy'>): boolean {
  return isManagedInConsent(u) && u.status === 'suspended' && u.suspendedBy !== 'core_role';
}

/** One line for the administrator after an invitation from a standalone tenant. */
export function describeInvitation(email: string, inv: AdminInvitationResult | undefined): string {
  if (!inv || !inv.sent) return `${email} was added, but the invitation email was not sent. Invite them again to resend it.`;
  if (inv.provider === 'mock') {
    return `${email} was added. Mail is not configured here: the invitation link was written to the controller log, not emailed.`;
  }
  return `Invitation emailed to ${email}. The link works once and expires${inv.expiresAt ? ` on ${new Date(inv.expiresAt).toLocaleString()}` : ''}.`;
}

function fmtDate(s: string | null): string {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

// ── Page ────────────────────────────────────────────────────────

export function AdminUsers(): JSX.Element {
  const [users, setUsers] = useState<DiscoveryUser[]>([]);
  const [consent, setConsent] = useState<AdminUsersConsentLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<DiscoveryUserStatus | ''>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [emailSearch, setEmailSearch] = useState('');

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('compliance_analyst');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // MFA reset dialog state (final gate of PR #84, F7).
  const [mfaResetTarget, setMfaResetTarget] = useState<DiscoveryUser | null>(null);
  const [mfaResetRequirePassword, setMfaResetRequirePassword] = useState(false);
  const [mfaResetSubmitting, setMfaResetSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const args: { status?: DiscoveryUserStatus; role?: string; limit?: number } = {
        limit: 500,
      };
      if (statusFilter) args.status = statusFilter;
      if (roleFilter) args.role = roleFilter;
      const r = await listAdminUsers(args);
      setUsers(r.users);
      setConsent(r.consent ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load users');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Client-side email search (server doesn't have a full-text param yet;
  // 500-row list cap means this is cheap enough)
  const filteredUsers = useMemo(() => {
    if (!emailSearch.trim()) return users;
    const needle = emailSearch.toLowerCase();
    return users.filter(
      (u) => u.email.toLowerCase().includes(needle) || u.fullName.toLowerCase().includes(needle),
    );
  }, [users, emailSearch]);

  // KPI counts (server-side total for active filter; client-side derived from list)
  const counts = useMemo(() => {
    const c = { total: users.length, active: 0, invited: 0, suspended: 0 };
    for (const u of users) {
      if (u.status === 'active') c.active += 1;
      else if (u.status === 'invited') c.invited += 1;
      else if (u.status === 'suspended') c.suspended += 1;
    }
    return c;
  }, [users]);

  // ── Action handlers ──

  async function handleInvite(): Promise<void> {
    setInviteSubmitting(true);
    setInviteError(null);
    try {
      const invited = await inviteAdminUser({
        email: inviteEmail.trim(),
        fullName: inviteFullName.trim(),
        role: inviteRole,
      });
      setNotice(describeInvitation(invited.user.email, invited.invitation));
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFullName('');
      setInviteRole('compliance_analyst');
      await refresh();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'failed to invite user');
      // A 502 means the account was created but the mail did not go: show it.
      await refresh();
    } finally {
      setInviteSubmitting(false);
    }
  }

  async function handleResend(user: DiscoveryUser): Promise<void> {
    setActionPending(user.id);
    try {
      const r = await inviteAdminUser({ email: user.email, fullName: user.fullName, role: user.role });
      setNotice(describeInvitation(user.email, r.invitation));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to resend the invitation');
    } finally {
      setActionPending(null);
    }
  }

  async function handleChangeRole(user: DiscoveryUser, nextRole: string): Promise<void> {
    setActionPending(user.id);
    try {
      await patchAdminUser(user.id, { role: nextRole });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to change role');
    } finally {
      setActionPending(null);
    }
  }

  async function handleToggleStatus(user: DiscoveryUser): Promise<void> {
    setActionPending(user.id);
    try {
      const next: DiscoveryUserStatus = user.status === 'suspended' ? 'active' : 'suspended';
      await patchAdminUser(user.id, { status: next });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to change status');
    } finally {
      setActionPending(null);
    }
  }

  function openResetMfa(user: DiscoveryUser): void {
    // The person enrols a new authenticator at their next sign-in. Use when
    // they lost their device, are locked out of the code step, or their
    // account may be in someone else's hands (then also require a new password).
    setMfaResetTarget(user);
    setMfaResetRequirePassword(false);
  }

  async function handleResetMfa(): Promise<void> {
    const user = mfaResetTarget;
    if (!user) return;
    setMfaResetSubmitting(true);
    setActionPending(user.id);
    try {
      const result = await resetAdminUserMfa(user.id, { requireNewPassword: mfaResetRequirePassword });
      setMfaResetTarget(null);
      setNotice(describeMfaReset(user.email, result));
      await refresh();
    } catch (e) {
      setMfaResetTarget(null);
      setError(e instanceof Error ? e.message : 'failed to reset MFA');
    } finally {
      setMfaResetSubmitting(false);
      setActionPending(null);
    }
  }

  async function handleDelete(user: DiscoveryUser): Promise<void> {
    // Soft-delete via DELETE endpoint (server sets status='suspended').
    // Confirmation via native confirm; production-grade swap to a modal.
    const warning = isUnsentInvitation(user, consent)
      ? `Soft-delete ${user.email}? The row is suspended, and that also stops this person opening FOCTTA Discovery from FOCTTA Consent until it is reinstated here. To let them in, leave it: their first hand-off from Consent takes it over.`
      : `Soft-delete ${user.email}? Their account will be suspended (account row preserved for audit retention).`;
    if (!window.confirm(warning)) {
      return;
    }
    setActionPending(user.id);
    try {
      await deleteAdminUser(user.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to delete user');
    } finally {
      setActionPending(null);
    }
  }

  // ── Render ──

  if (loading && users.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="Users" subtitle="Manage tenant users + roles" />
        <LoadingState caption="Loading users…" />
      </PageContainer>
    );
  }

  if (error && users.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="Users" subtitle="Manage tenant users + roles" />
        <ErrorState
          title="Couldn't load users"
          detail={error}
          onRetry={refresh}
          severity="critical"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Users"
        subtitle={
          consent?.linked
            ? 'People and roles are managed in FOCTTA Consent. Everyone who opens FOCTTA Discovery from Consent is listed here.'
            : 'Manage tenant users + roles. An invited user gets an email with a link that works once; they set their password with it.'
        }
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {consent?.linked ? (
              consent.peopleUrl ? (
                <Button size="sm" asChild>
                  <a href={consent.peopleUrl} target="_blank" rel="noopener noreferrer" data-testid="invite-in-consent">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Invite in FOCTTA Consent
                  </a>
                </Button>
              ) : (
                <Button size="sm" disabled title="Invite people in FOCTTA Consent" data-testid="invite-in-consent">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Invite in FOCTTA Consent
                </Button>
              )
            ) : (
              <Button size="sm" onClick={() => setInviteOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Invite user
              </Button>
            )}
          </div>
        }
      />

      {/* KPI tiles. KpiCard takes a numeric value + optional glow for
          severity tone (critical/high/medium/low). Total stays neutral
          (no glow); the lifecycle states pick a tone that matches
          their operational meaning. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total" value={counts.total} icon={UsersIcon} />
        <KpiCard label="Active" value={counts.active} icon={UserCheck} glow="low" />
        <KpiCard
          label="Invited (pending)"
          value={counts.invited}
          icon={Clock}
          glow="medium"
        />
        <KpiCard
          label="Suspended"
          value={counts.suspended}
          icon={Ban}
          glow={counts.suspended > 0 ? 'high' : undefined}
        />
      </div>

      {/* Consent owns people and roles for a linked tenant (30 Oct 2026). */}
      {consent?.linked ? (
        <GlassCard className="mb-6" data-testid="managed-in-consent-banner">
          <div className="p-4 flex items-start gap-2 text-sm">
            <Shield className="h-4 w-4 mt-0.5 text-sky-600 dark:text-sky-400" />
            <span>
              This organisation&apos;s people and roles are managed in FOCTTA Consent. Invite people
              and change roles there; each person appears here, read-only, the first time they open
              FOCTTA Discovery from Consent, and their role is updated each time.
              {consent.peopleUrl ? (
                <>
                  {' '}
                  <a className="underline" href={consent.peopleUrl} target="_blank" rel="noopener noreferrer">
                    Open people in FOCTTA Consent
                  </a>
                </>
              ) : (
                <span data-testid="consent-address-unknown">
                  {' '}
                  FOCTTA Discovery does not know FOCTTA Consent&apos;s address here: open Consent as you
                  usually do, then Settings, Team.
                </span>
              )}
            </span>
          </div>
        </GlassCard>
      ) : null}

      {/* Filters */}
      <GlassCard className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Filter</CardTitle>
          <CardDescription clamp>Narrow the list by status, role, or email/name search.</CardDescription>
        </CardHeader>
        <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Status</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DiscoveryUserStatus | '')}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Role</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All</option>
              {SELECTABLE_ROLES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Search email/name</label>
            <input
              type="text"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder="e.g. alice@example.com"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
            />
          </div>
        </div>
      </GlassCard>

      {/* What the last MFA reset did */}
      {notice ? (
        <GlassCard className="mb-4">
          <div className="p-4 flex items-center gap-2 text-sm" role="status">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span>{notice}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setNotice(null)}>
              Dismiss
            </Button>
          </div>
        </GlassCard>
      ) : null}

      {/* Non-fatal error banner */}
      {error && users.length > 0 ? (
        <GlassCard className="mb-4 border-destructive/30">
          <div className="p-4 flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </GlassCard>
      ) : null}

      {/* Users table */}
      <GlassCard>
        <CardHeader>
          <CardTitle>
            {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}
            {emailSearch || statusFilter || roleFilter ? (
              <span className="ml-2 text-xs text-muted-foreground">
                (filtered from {users.length})
              </span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <div className="px-6 pb-6 overflow-x-auto">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No users match your filters.
              {(statusFilter || roleFilter || emailSearch) && (
                <Button
                  variant="link"
                  className="ml-2"
                  onClick={() => {
                    setStatusFilter('');
                    setRoleFilter('');
                    setEmailSearch('');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="pb-3 pr-4 font-medium">User</th>
                  <th className="pb-3 pr-4 font-medium">Role</th>
                  <th className="pb-3 pr-4 font-medium">Status</th>
                  <th className="pb-3 pr-4 font-medium">Invited</th>
                  <th className="pb-3 pr-4 font-medium">Last login</th>
                  <th className="pb-3 pr-4 font-medium">MFA</th>
                  <th className="pb-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const StatusIcon = STATUS_ICON[u.status];
                  const isBusy = actionPending === u.id;
                  return (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border-b last:border-b-0 hover:bg-muted/30"
                    >
                      <td className="py-3 pr-4">
                        <div className="font-medium">{u.fullName || '-'}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {u.email}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        {isManagedInConsent(u) ? (
                          <div className="text-xs" data-testid="managed-role">
                            <div>{ROLE_LABEL[u.role] ?? u.role}</div>
                            {consent?.peopleUrl ? (
                              <a
                                className="text-muted-foreground underline inline-flex items-center gap-1"
                                href={consent.peopleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Managed in FOCTTA Consent
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="text-muted-foreground">Managed in FOCTTA Consent</span>
                            )}
                          </div>
                        ) : (
                        <select
                          className="rounded-md border bg-background px-2 py-1 text-xs"
                          value={u.role}
                          disabled={isBusy}
                          onChange={(e) => handleChangeRole(u, e.target.value)}
                          data-action="disc.adm_users.a02"
                        >
                          {SELECTABLE_ROLES.map((r) => (
                            <option key={r.key} value={r.key}>
                              {r.label}
                            </option>
                          ))}
                          {/* If user has a role outside the selectable list (e.g. platform_*),
                              show it as a disabled option so we don't silently change it. */}
                          {!ROLE_LABEL[u.role] && (
                            <option value={u.role} disabled>
                              {u.role} (platform)
                            </option>
                          )}
                        </select>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={STATUS_VARIANT[u.status]} className="gap-1">
                          <StatusIcon className="h-3 w-3" />
                          {u.status}
                        </Badge>
                        {isUnsentInvitation(u, consent) ? (
                          <div
                            className="text-xs text-muted-foreground mt-1"
                            data-testid="never-activated"
                            title="Created here before invitations were sent. Invite them in FOCTTA Consent: this row becomes their account the first time they open FOCTTA Discovery from Consent."
                          >
                            Never activated: no invitation was sent
                          </div>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {fmtDate(u.invitedAt)}
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {fmtDate(u.lastLoginAt)}
                      </td>
                      <td className="py-3 pr-4 text-xs">
                        {(() => {
                          // A Consent-managed person signs in through Consent:
                          // no Discovery factor of theirs to reset here.
                          const action = isManagedInConsent(u) ? null : mfaAdminAction(u);
                          const locked =
                            !!u.mfaLockedUntil && new Date(u.mfaLockedUntil).getTime() > Date.now();
                          return (
                            <span className="inline-flex items-center gap-1">
                              {u.mfaEnrolledAt ? (
                                <Badge variant="success" className="gap-1">
                                  <Shield className="h-3 w-3" />
                                  On
                                </Badge>
                              ) : u.mfaSecretStaged ? (
                                <Badge variant="warning" className="gap-1" title="Set-up started but never confirmed with a code">
                                  <Clock className="h-3 w-3" />
                                  Set-up incomplete
                                </Badge>
                              ) : (
                                <Badge variant="glass" className="gap-1 text-muted-foreground">
                                  <XCircle className="h-3 w-3" />
                                  Off
                                </Badge>
                              )}
                              {locked ? (
                                <Badge
                                  variant="critical"
                                  className="gap-1"
                                  title={`Too many wrong codes. Locked until ${new Date(u.mfaLockedUntil ?? Date.now()).toLocaleString()}`}
                                >
                                  <Lock className="h-3 w-3" />
                                  Locked
                                </Badge>
                              ) : null}
                              {action ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={isBusy}
                                  onClick={() => openResetMfa(u)}
                                  title={
                                    action === 'reset'
                                      ? 'Reset MFA (they set it up again at next sign-in)'
                                      : 'Clear MFA lock / enrolment (they set up MFA at next sign-in)'
                                  }
                                  aria-label={
                                    action === 'reset'
                                      ? `Reset MFA for ${u.email}`
                                      : `Clear MFA lock / enrolment for ${u.email}`
                                  }
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </Button>
                              ) : null}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 text-right">
                        {isManagedInConsent(u) ? (
                          mayReinstateManaged(u) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isBusy}
                              onClick={() => handleToggleStatus(u)}
                              title="Reinstate"
                              aria-label={`Reinstate ${u.email}`}
                              data-action="disc.adm_users.a03"
                              data-testid="reinstate-managed"
                            >
                              {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">Change in FOCTTA Consent</span>
                          )
                        ) : (
                        <div className="flex justify-end gap-1">
                          {!consent?.linked && u.status === 'invited' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isBusy}
                              onClick={() => handleResend(u)}
                              title="Resend invitation"
                              aria-label={`Resend invitation to ${u.email}`}
                              data-action="disc.adm_users.a01"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => handleToggleStatus(u)}
                            title={u.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                            data-action="disc.adm_users.a03"
                          >
                            {isBusy ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : u.status === 'suspended' ? (
                              <UserCheck className="h-4 w-4" />
                            ) : (
                              <Ban className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => handleDelete(u)}
                            title="Soft-delete"
                            className="text-destructive hover:text-destructive"
                            data-action="disc.adm_users.a04"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </GlassCard>

      {/* Invite dialog (lightweight inline modal - no Radix dependency
          to keep this page self-contained; matches the pattern of
          other admin pages that use native modals for simple flows). */}
      {inviteOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setInviteOpen(false);
          }}
        >
          <GlassCard className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Invite user
              </CardTitle>
              <CardDescription clamp>
                Creates the account and emails them a link that works once. They set their
                password with it to activate the account.
              </CardDescription>
            </CardHeader>
            <div className="px-6 pb-6 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Email</label>
                <input
                  type="email"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="alice@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Full name</label>
                <input
                  type="text"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="Alice Sharma"
                  value={inviteFullName}
                  onChange={(e) => setInviteFullName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Role</label>
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {SELECTABLE_ROLES.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              {inviteError ? (
                <div className="text-sm text-destructive flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {inviteError}
                </div>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setInviteOpen(false)}
                  disabled={inviteSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInvite}
                  disabled={
                    inviteSubmitting ||
                    !inviteEmail.trim() ||
                    !inviteFullName.trim() ||
                    !inviteRole
                  }
                  data-action="disc.adm_users.a01"
                >
                  {inviteSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Inviting…
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Send invite
                    </>
                  )}
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      ) : null}

      {/* MFA reset dialog (final gate of PR #84, F7). The option to require a
          new password is off by default and says exactly what it does. */}
      {mfaResetTarget ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !mfaResetSubmitting) setMfaResetTarget(null);
          }}
        >
          <GlassCard className="w-full max-w-md" role="dialog" aria-label="Reset MFA">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5" />
                {mfaAdminAction(mfaResetTarget) === 'clear'
                  ? `Clear MFA lock / enrolment for ${mfaResetTarget.email}`
                  : `Reset MFA for ${mfaResetTarget.email}`}
              </CardTitle>
              <CardDescription>
                Their authenticator, backup codes and any MFA lock are removed, and every
                sign-in link already sent to them stops working. They set up MFA again at
                their next sign-in.
              </CardDescription>
            </CardHeader>
            <div className="px-6 pb-6 space-y-4">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={mfaResetRequirePassword}
                  onChange={(e) => setMfaResetRequirePassword(e.target.checked)}
                  disabled={mfaResetSubmitting}
                />
                <span>
                  Also require a new password
                  <span className="block text-xs text-muted-foreground">
                    Clears their password now and emails them a reset link. Use this if the
                    account may be in someone else&apos;s hands. If their mailbox may be too,
                    suspend the account instead until it is secured.
                  </span>
                </span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setMfaResetTarget(null)}
                  disabled={mfaResetSubmitting}
                >
                  Cancel
                </Button>
                <Button onClick={handleResetMfa} disabled={mfaResetSubmitting}>
                  {mfaResetSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {mfaResetRequirePassword ? 'Reset MFA and password' : 'Reset MFA'}
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      ) : null}
    </PageContainer>
  );
}

export default AdminUsers;
